/**
 * TanStack Query hooks for Subject Identity management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { isApiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import {
  fetchSubjects,
  fetchSubject,
  createSubject,
  updateSubject,
  deleteSubject,
} from '@/lib/uas-api'
import type {
  SubjectQueryParams,
  CreateSubjectRequest,
  UpdateSubjectRequest,
} from '@/types/uas'

export const subjectKeys = {
  all: ['uas', 'subjects'] as const,
  lists: () => [...subjectKeys.all, 'list'] as const,
  list: (params: SubjectQueryParams) => [...subjectKeys.lists(), params] as const,
  details: () => [...subjectKeys.all, 'detail'] as const,
  detail: (id: string) => [...subjectKeys.details(), id] as const,
}

export function useSubjects(params: SubjectQueryParams = {}) {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: subjectKeys.list(params),
    queryFn: () => fetchSubjects(params),
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
    enabled: !!tenantId,
  })
}

export function useSubject(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? subjectKeys.detail(id)
      : ['uas', 'subjects', 'detail', 'disabled'],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Subject ID is required'))
      return fetchSubject(id)
    },
    enabled: !!id,
    retry: false,
    staleTime: 10 * 1000,
  })
}

export function useCreateSubject() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: CreateSubjectRequest) => createSubject(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() })
      toast.success('Subject created successfully')
      navigate(`/identity/subjects/${data.id}`)
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to create subject: ${message}`)
    },
  })
}

export function useUpdateSubject(subjectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateSubjectRequest) => updateSubject(subjectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subjectKeys.detail(subjectId) })
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() })
      toast.success('Subject updated successfully')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to update subject: ${message}`)
    },
  })
}

export function useDeleteSubject() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (id: string) => deleteSubject(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: subjectKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() })
      navigate('/identity/subjects')
      toast.success('Subject deactivated successfully')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to deactivate subject: ${message}`)
    },
  })
}
